/**
 * Safety alerts.
 *
 * Anchor watch (centre + radius, alarms on swing exceeded), MOB (drop mark at
 * ownship now), safety contour/depth (boundary depth used to flag shallow
 * features as dangerous), danger look-ahead (cone ahead of ownship, flags
 * wrecks/obstructions/shallow inside the cone).
 */
import {
  rhumbBearingDeg,
  rhumbDestination,
  rhumbDistanceNm,
} from '@/nav';
import type { ChartFeature } from '@/types';
import { featuresIn, bboxAround, legBBox, type FeatureSource } from './chartIndex';
export * from './chartIndex';

export interface AnchorWatch {
  centre: [number, number];
  radiusNm: number;
  setAt: number;
}

export interface MobMark {
  position: [number, number];
  droppedAt: number;
}

export interface SafetySettings {
  contourM: number;
  depthM: number;
  /**
   * Master switch for the danger look-ahead cone. Off hides the cone and stops
   * the arc scan; the grounding alarm keeps its own switch. On by default so the
   * safety net a skipper already relies on doesn't vanish under a new toggle.
   */
  coneOn: boolean;
  /**
   * How the cone reach is set:
   *   'time'     — how far we'd travel in `lookaheadMin` at the current SOG.
   *   'distance' — a fixed `lookaheadNm` ahead, whatever the speed.
   * Time follows the boat (a fast passage looks further ahead); distance is a
   * steady reach that still shows something when we're barely moving.
   */
  lookaheadMode: 'time' | 'distance';
  lookaheadMin: number;
  /** Fixed reach ahead in distance mode, nautical miles. */
  lookaheadNm: number;
  lookaheadHalfAngleDeg: number;
  /**
   * GNSS horizontal-accuracy alarm threshold, metres. When the receiver's
   * estimated position error exceeds this, the poor-accuracy banner fires.
   * Default 15 m: a coastal fix is normally a few metres, so 15 m or worse is
   * a genuinely degraded fix worth flagging.
   */
  gnssAccuracyAlarmM: number;
}

export const DEFAULT_SAFETY: SafetySettings = {
  contourM: 10,
  depthM: 5,
  coneOn: true,
  lookaheadMode: 'time',
  lookaheadMin: 6,
  lookaheadNm: 0.5,
  lookaheadHalfAngleDeg: 15,
  gnssAccuracyAlarmM: 15,
};

/**
 * Shortest cone reach we'll draw in time mode, nautical miles. At a near-stop the
 * time projection collapses to nothing, which would hide a danger right under the
 * bow, so we hold a small fixed reach instead of a zero-length cone. About a
 * cable (0.1 nm) — enough to still flag what's immediately ahead.
 */
export const LOOKAHEAD_MIN_NM = 0.1;

/**
 * The cone reach ahead, nautical miles, honouring the mode:
 *   distance — the set nm, flat.
 *   time     — SOG x minutes, but never shorter than LOOKAHEAD_MIN_NM so a
 *              stopped boat still gets a stub cone rather than a blank.
 * Pure, so the danger scan, the drawn cone and the grounding horizon all read the
 * exact same number.
 */
export function lookaheadDistanceNm(sog: number, settings: SafetySettings): number {
  if (settings.lookaheadMode === 'distance') return Math.max(0, settings.lookaheadNm);
  const d = sog * (settings.lookaheadMin / 60);
  return d > LOOKAHEAD_MIN_NM ? d : LOOKAHEAD_MIN_NM;
}

export interface AnchorWatchStatus {
  rangeNm: number;
  alarm: boolean;
}

export function checkAnchorWatch(
  watch: AnchorWatch | null,
  ownshipPosition: [number, number] | null,
): AnchorWatchStatus | null {
  if (!watch || !ownshipPosition) return null;
  const range = rhumbDistanceNm(watch.centre, ownshipPosition);
  return { rangeNm: range, alarm: range > watch.radiusNm };
}

export interface MobStatus {
  rangeNm: number;
  bearingDeg: number;
  elapsedMs: number;
}

export function checkMob(
  mob: MobMark | null,
  ownshipPosition: [number, number] | null,
  now: number = Date.now(),
): MobStatus | null {
  if (!mob || !ownshipPosition) return null;
  return {
    rangeNm: rhumbDistanceNm(ownshipPosition, mob.position),
    bearingDeg: rhumbBearingDeg(ownshipPosition, mob.position),
    elapsedMs: now - mob.droppedAt,
  };
}

export interface DangerHit {
  feature: ChartFeature;
  rangeNm: number;
  bearingDeg: number;
  reason: 'wreck' | 'obstruction' | 'shallow' | 'isolated_danger';
}

export function dangersAhead(
  ownship: { position: [number, number]; cog: number; sog: number } | null,
  cells: FeatureSource,
  settings: SafetySettings,
): DangerHit[] {
  if (!ownship || !settings.coneOn) return [];
  const distanceNm = lookaheadDistanceNm(ownship.sog, settings);
  if (distanceNm <= 0) return [];
  const halfAngle = settings.lookaheadHalfAngleDeg;
  const out: DangerHit[] = [];
  for (const f of featuresIn(cells, bboxAround(ownship.position, distanceNm))) {
    const point = featureFocalPoint(f);
    if (!point) continue;
    const range = rhumbDistanceNm(ownship.position, point);
    if (range > distanceNm) continue;
    const bearing = rhumbBearingDeg(ownship.position, point);
    const diff = bearingDiff(bearing, ownship.cog);
    if (Math.abs(diff) > halfAngle) continue;
    const reason = dangerReason(f, settings);
    if (!reason) continue;
    out.push({ feature: f, rangeNm: range, bearingDeg: bearing, reason });
  }
  out.sort((a, b) => a.rangeNm - b.rangeNm);
  return out;
}

function featureFocalPoint(f: ChartFeature): [number, number] | null {
  const g = f.geometry;
  switch (g.type) {
    case 'Point':
      return [g.coordinates[0], g.coordinates[1]];
    case 'LineString': {
      const c = g.coordinates;
      if (!c.length) return null;
      const mid = c[Math.floor(c.length / 2)];
      return [mid[0], mid[1]];
    }
    case 'Polygon': {
      const ring = g.coordinates[0];
      if (!ring?.length) return null;
      let sx = 0;
      let sy = 0;
      for (const p of ring) {
        sx += p[0];
        sy += p[1];
      }
      return [sx / ring.length, sy / ring.length];
    }
    default:
      return null;
  }
}

function dangerReason(f: ChartFeature, settings: SafetySettings): DangerHit['reason'] | null {
  const p = f.properties;
  if (p.group === 'wreck') return 'wreck';
  if (p.group === 'obstruction') return 'obstruction';
  const attrs = (p.attrs ?? {}) as Record<string, unknown>;
  if (attrs.category === 'isolated_danger') return 'isolated_danger';
  if (p.objClass === 'DEPARE') {
    const dmin = typeof attrs.depthMinM === 'number' ? attrs.depthMinM : null;
    const dmax = typeof attrs.depthMaxM === 'number' ? attrs.depthMaxM : null;
    if (dmax !== null && dmax <= settings.depthM) return 'shallow';
    if (dmin !== null && dmin <= 0) return 'shallow';
  }
  if (p.group === 'sounding') {
    const d = typeof attrs.depthM === 'number' ? attrs.depthM : null;
    if (d !== null && d < settings.depthM) return 'shallow';
  }
  return null;
}

function bearingDiff(a: number, b: number): number {
  let d = ((a - b + 540) % 360) - 180;
  if (d <= -180) d += 360;
  return d;
}

export function anchorRing(watch: AnchorWatch): GeoJSON.Feature {
  const pts: [number, number][] = [];
  const steps = 72;
  for (let i = 0; i <= steps; i++) {
    pts.push(rhumbDestination(watch.centre, (i * 360) / steps, watch.radiusNm));
  }
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [pts] },
    properties: { kind: 'anchor-ring' },
  };
}

export function lookaheadCone(
  position: [number, number],
  cog: number,
  sog: number,
  settings: SafetySettings,
): GeoJSON.Feature | null {
  if (!settings.coneOn) return null;
  const distanceNm = lookaheadDistanceNm(sog, settings);
  if (distanceNm <= 0) return null;
  const half = settings.lookaheadHalfAngleDeg;
  const left = rhumbDestination(position, cog - half, distanceNm);
  const right = rhumbDestination(position, cog + half, distanceNm);
  const steps = 16;
  const arc: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const b = cog - half + (2 * half * i) / steps;
    arc.push(rhumbDestination(position, b, distanceNm));
  }
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [[position, ...arc, position]] },
    properties: { kind: 'lookahead', left, right },
  };
}

// -----------------------------------------------------------------------------
// Proximity ring + cross-track corridor recommendation
// -----------------------------------------------------------------------------

/** A circle of `radiusNm` around `centre`, as a closed ring of [lon, lat]. */
export function circleRing(
  centre: [number, number],
  radiusNm: number,
  steps = 64,
): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    pts.push(rhumbDestination(centre, (i * 360) / steps, radiusNm));
  }
  return pts;
}

/**
 * Perpendicular distance (nm) from point p to the segment a-b, and whether the
 * foot lies within the segment. Flat-earth approximation, fine at leg scale.
 */
function perpToLeg(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): { distNm: number; within: boolean } {
  const k = Math.cos((a[1] * Math.PI) / 180);
  const ax = a[0] * k;
  const bx = b[0] * k;
  const px = p[0] * k;
  const vx = bx - ax;
  const vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  let t = len2 === 0 ? 0 : ((px - ax) * vx + (p[1] - a[1]) * vy) / len2;
  const within = t >= -0.05 && t <= 1.05;
  t = Math.max(0, Math.min(1, t));
  const fx = ax + t * vx;
  const fy = a[1] + t * vy;
  return { distNm: Math.hypot(px - fx, p[1] - fy) * 60, within };
}

/**
 * Recommend a cross-track-distance limit for a leg, from charted dangers near
 * it. We find the nearest danger (wreck, obstruction, shallow, isolated danger,
 * as classified for the look-ahead) whose perpendicular foot falls on the leg,
 * within a search window, and set the corridor to keep a margin clear of it.
 *
 * Honest about its limits: this is a coarse proximity heuristic on the training
 * cells, not a swept-path clearance analysis. It only looks at point/area focal
 * points, so a long shoal counts as its centroid. Treat it as a starting
 * suggestion to sanity-check, not an authority. Returns null when nothing
 * relevant is near.
 */
export function recommendXtdForLeg(
  legStart: [number, number],
  legEnd: [number, number],
  cells: FeatureSource,
  settings: SafetySettings,
  searchNm = 1.0,
): number | null {
  let nearest = Infinity;
  for (const f of featuresIn(cells, legBBox(legStart, legEnd, searchNm + 0.1))) {
    if (!dangerReason(f, settings)) continue;
    const point = featureFocalPoint(f);
    if (!point) continue;
    const { distNm, within } = perpToLeg(point, legStart, legEnd);
    if (!within || distNm > searchNm) continue;
    if (distNm < nearest) nearest = distNm;
  }
  if (!Number.isFinite(nearest)) return null;
  // Keep ~20% margin inside the nearest danger, clamped to sensible bounds.
  const rec = nearest * 0.8;
  return Math.max(0.05, Math.min(rec, 0.5));
}

// -----------------------------------------------------------------------------
// Underway alarms: charted-hazard proximity and charted depth under the boat
// -----------------------------------------------------------------------------

/**
 * Charted dangers within `radiusNm` of the boat, nearest first. Same hazard
 * classification as the look-ahead (wrecks, obstructions, shallow areas and
 * soundings, isolated dangers), but a plain range ring rather than a cone, so
 * it catches a danger off the beam or astern too, not only dead ahead.
 *
 * Honest about its limits: it measures to each feature's focal point (a point's
 * position, a line or area's centre), so a long shoal counts as its middle. A
 * proximity prompt to look at the chart, not a swept clearance.
 */
export function dangersWithin(
  ownshipPosition: [number, number] | null,
  cells: FeatureSource,
  settings: SafetySettings,
  radiusNm: number,
): DangerHit[] {
  if (!ownshipPosition || radiusNm <= 0) return [];
  const out: DangerHit[] = [];
  for (const f of featuresIn(cells, bboxAround(ownshipPosition, radiusNm + 0.05))) {
    const reason = dangerReason(f, settings);
    if (!reason) continue;
    const point = featureFocalPoint(f);
    if (!point) continue;
    const range = rhumbDistanceNm(ownshipPosition, point);
    if (range > radiusNm) continue;
    out.push({ feature: f, rangeNm: range, bearingDeg: rhumbBearingDeg(ownshipPosition, point), reason });
  }
  out.sort((a, b) => a.rangeNm - b.rangeNm);
  return out;
}

function pointInRing(pt: [number, number], ring: number[][]): boolean {
  let inside = false;
  const x = pt[0];
  const y = pt[1];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const hit = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring and not inside any hole. */
function pointInPolygon(pt: [number, number], rings: number[][][]): boolean {
  if (!rings.length || !pointInRing(pt, rings[0])) return false;
  for (let i = 1; i < rings.length; i++) {
    if (pointInRing(pt, rings[i])) return false;
  }
  return true;
}

/**
 * The charted depth under the boat, metres, or null if the chart can't tell us
 * here. We take the depth area the boat sits in (its shallow end, DRVAL1, the
 * least charted depth of that area) and any sounding within a short reach, and
 * return the shallowest of them, because the cautious figure is the one worth
 * alarming on.
 *
 * This is the charted depth, not a live echo-sounder reading, and a depth area
 * only gives the range for the whole area, so it can be cautious. Treat it as
 * "the chart says it's shoaling here", a prompt to check the sounder and the
 * chart, not a measured depth under the keel.
 */
export function chartedDepthUnderBoat(
  ownshipPosition: [number, number] | null,
  cells: FeatureSource,
  soundingSearchNm = 0.05,
): number | null {
  if (!ownshipPosition) return null;
  let shallow: number | null = null;
  const consider = (d: number | null | undefined) => {
    if (d == null || !Number.isFinite(d)) return;
    if (shallow == null || d < shallow) shallow = d;
  };
  for (const f of featuresIn(cells, bboxAround(ownshipPosition, soundingSearchNm + 0.05))) {
    {
      const p = f.properties;
      const attrs = (p.attrs ?? {}) as Record<string, unknown>;
      const g = f.geometry;
      if (p.objClass === 'DEPARE') {
        const dmin = typeof attrs.depthMinM === 'number' ? attrs.depthMinM : null;
        const dmax = typeof attrs.depthMaxM === 'number' ? attrs.depthMaxM : null;
        const depth = dmin ?? dmax;
        if (depth == null) continue;
        let here = false;
        if (g.type === 'Polygon') {
          here = pointInPolygon(ownshipPosition, g.coordinates as number[][][]);
        } else if (g.type === 'MultiPolygon') {
          here = (g.coordinates as number[][][][]).some((poly) =>
            pointInPolygon(ownshipPosition, poly),
          );
        }
        if (here) consider(depth);
      } else if (p.group === 'sounding' && g.type === 'Point') {
        const d = typeof attrs.depthM === 'number' ? attrs.depthM : null;
        if (d == null) continue;
        const c = g.coordinates as number[];
        if (rhumbDistanceNm(ownshipPosition, [c[0], c[1]]) <= soundingSearchNm) consider(d);
      }
    }
  }
  return shallow;
}

/**
 * What the chart actually says about the depth at a point, kept as separate parts
 * rather than boiled down to one number.
 *
 * chartedDepthUnderBoat above returns a single cautious figure, which is right for
 * an alarm. It is wrong for a readout. A depth area only carries a RANGE (DRVAL1
 * to DRVAL2), so taking its shallow end and printing "0.0 m" tells somebody there
 * is no water under them when the area is really the 0 to 18 m band and the
 * soundings around are 12 and 15. Worse, cells of different scales overlap, so a
 * coarse approach-cell band can sit under a detailed harbour one and drag that
 * single figure down. Handing back the pieces lets the readout say what it knows.
 */
export interface ChartedDepthDetail {
  /** Shallow end of the depth area containing the point (DRVAL1), or null. */
  areaMinM: number | null;
  /** Deep end of that same area (DRVAL2), or null when the cell doesn't carry it. */
  areaMaxM: number | null;
  /** Nearest charted sounding within the search reach, metres, or null. */
  soundingM: number | null;
  /** How far that sounding is from the point, nm, or null when there isn't one. */
  soundingDistNm: number | null;
}

/**
 * The charted depth picture at a point: the depth area it sits in and the nearest
 * sounding to it. Where two depth areas overlap we keep the shallower one, the
 * same cautious choice chartedDepthUnderBoat makes, but here it is reported as the
 * range it really is rather than as a single figure.
 */
export function chartedDepthDetailAt(
  pos: [number, number] | null,
  cells: FeatureSource,
  soundingSearchNm = 0.05,
): ChartedDepthDetail {
  const out: ChartedDepthDetail = {
    areaMinM: null,
    areaMaxM: null,
    soundingM: null,
    soundingDistNm: null,
  };
  if (!pos) return out;

  for (const f of featuresIn(cells, bboxAround(pos, soundingSearchNm + 0.05))) {
    const p = f.properties;
    const attrs = (p.attrs ?? {}) as Record<string, unknown>;
    const g = f.geometry;

    if (p.objClass === 'DEPARE') {
      const dmin = typeof attrs.depthMinM === 'number' ? attrs.depthMinM : null;
      const dmax = typeof attrs.depthMaxM === 'number' ? attrs.depthMaxM : null;
      if (dmin == null && dmax == null) continue;
      let here = false;
      if (g.type === 'Polygon') {
        here = pointInPolygon(pos, g.coordinates as number[][][]);
      } else if (g.type === 'MultiPolygon') {
        here = (g.coordinates as number[][][][]).some((poly) => pointInPolygon(pos, poly));
      }
      if (!here) continue;
      // Keep the shallower area when bands overlap, and carry its own deep end with
      // it so the pair always describes one real area rather than two mixed up.
      const key = dmin ?? dmax;
      const bestKey = out.areaMinM ?? out.areaMaxM;
      if (key != null && (bestKey == null || key < bestKey)) {
        out.areaMinM = dmin;
        out.areaMaxM = dmax;
      }
    } else if (p.group === 'sounding' && g.type === 'Point') {
      const d = typeof attrs.depthM === 'number' ? attrs.depthM : null;
      if (d == null) continue;
      const c = g.coordinates as number[];
      const dist = rhumbDistanceNm(pos, [c[0], c[1]]);
      if (dist > soundingSearchNm) continue;
      if (out.soundingDistNm == null || dist < out.soundingDistNm) {
        out.soundingM = d;
        out.soundingDistNm = dist;
      }
    }
  }
  return out;
}

/**
 * Depth under the keel, metres: the charted depth (relative to chart datum) plus
 * the height of tide now, less the boat's draught. `tideM` null (no height
 * prediction available) is treated as zero tide, i.e. chart datum, the cautious
 * reading. Returns null when there's no charted depth to work from.
 */
export function depthUnderKeel(
  chartedM: number | null,
  tideM: number | null,
  draughtM: number,
): number | null {
  if (chartedM == null) return null;
  return chartedM + (tideM ?? 0) - draughtM;
}

/**
 * Whether a drying area is covered by the tide now. A drying patch dries to a
 * height of (−depthMinM) above chart datum (depthMinM is negative for a drying
 * area), so it is covered once the height of tide exceeds that drying height.
 * Mirrors the paint expression used to shade drying areas on the chart.
 */
export function dryingCovered(heightNowM: number | null, depthMinM: number): boolean {
  return (heightNowM ?? 0) + depthMinM > 0;
}

// -----------------------------------------------------------------------------
// Route hazard scan (planning): charted dangers near a plotted route's track
// -----------------------------------------------------------------------------

export interface RouteHazard {
  /** Leg the hazard is nearest to (0-based: waypoint i to i+1). */
  legIndex: number;
  feature: ChartFeature;
  reason: DangerHit['reason'];
  /** Perpendicular distance from the track, nm. */
  distNm: number;
  /** The feature's focal point [lon, lat]. */
  position: [number, number];
}

/**
 * Charted dangers within a corridor either side of a plotted route, for the
 * "check the route" step. Same hazard classification as the look-ahead (wrecks,
 * obstructions, shallow areas/soundings, isolated dangers); a feature near more
 * than one leg is kept once, at its closest. Nearest first.
 *
 * Honest about its limits, same as the underway checks: it measures to each
 * feature's focal point, so a long shoal counts as its centroid. A prompt to
 * zoom in and look, not a swept-path clearance.
 */
export function scanRouteHazards(
  waypoints: [number, number][],
  cells: FeatureSource,
  settings: SafetySettings,
  corridorNm = 0.5,
): RouteHazard[] {
  const best = new Map<string, RouteHazard>();
  for (let i = 0; i + 1 < waypoints.length; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    for (const f of featuresIn(cells, legBBox(a, b, corridorNm + 0.1))) {
      {
        const reason = dangerReason(f, settings);
        if (!reason) continue;
        const point = featureFocalPoint(f);
        if (!point) continue;
        const { distNm, within } = perpToLeg(point, a, b);
        if (!within || distNm > corridorNm) continue;
        const id = String(f.properties?.id ?? `${point[0]},${point[1]}`);
        const prev = best.get(id);
        if (!prev || distNm < prev.distNm) {
          best.set(id, { legIndex: i, feature: f, reason, distNm, position: point });
        }
      }
    }
  }
  return [...best.values()].sort((x, y) => x.distNm - y.distNm);
}

/**
 * Is a point navigable for a boat of this draught: not inside charted land, and
 * the charted depth (plus the height of tide, less draught and a clearance
 * margin) leaves water under the keel. Where the chart has no depth here, we
 * assume it's navigable (open water with no shoal charted). Used by the
 * auto-router to block grid nodes; same coarse, charted-not-surveyed caveat.
 */
export function navigableAt(
  pos: [number, number],
  cells: FeatureSource,
  draughtM: number,
  clearanceM: number,
  tideHeightM = 0,
): boolean {
  for (const f of featuresIn(cells, bboxAround(pos, 0.03))) {
    {
      if (f.properties.group !== 'land') continue;
      const g = f.geometry;
      if (g.type === 'Polygon' && pointInPolygon(pos, g.coordinates as number[][][])) return false;
      if (
        g.type === 'MultiPolygon' &&
        (g.coordinates as number[][][][]).some((poly) => pointInPolygon(pos, poly))
      ) {
        return false;
      }
    }
  }
  const charted = chartedDepthUnderBoat(pos, cells, 0.04);
  if (charted == null) return true;
  return charted + tideHeightM - draughtM - clearanceM >= 0;
}

export * from './anchorWatch';
export * from './anchorNative';
export * from './watchSnapshots';
export * from './grounding';
export * from './mobDrift';
export * from './mobLeeway';
export * from './searchPatterns';
export * from './mobSearchArea';

export * from './anchorScope';

export * from './anchorReadout';

export * from './batteryAlarm';

export * from './alarmState';
export * from './alarmAck';

export * from './backgroundService';
export * from './backgroundAlarms';
export * from './watchFlag';
export * from './routeAlarms';
